import * as React from "react";
import * as ReactDOM from "react-dom";
import * as moment from "moment";
import { CalendarViewMode } from "../../enumerations";
import * as modelsGet from "../../models/get";
import { ISelectedObject } from "../../models/calendar";
import { getGridChildStyle } from "../../utils/utils";
import { OptionalLink } from "../optionalLink";
import { Appointment } from "./appointment";
import { BlockedAppointment } from "./blockedAppointment";
import { PopupProvider, Popup, PopupTrigger, PopupAlign } from "../popup";

interface IProperties
{
    /**
     * Inline-Style.
     */
    style?: React.CSSProperties,

    /**
     * Die momentane Route.
     */
    route: () => uri.URI,

    /**
     * Der angemeldete Benutzer.
     */
    currentUser: modelsGet.IUser,

    /**
     * Der momentan selektierte Kalender oder die Kalendergruppe.
     */
    selectedObject: ISelectedObject,

    /**
     * Der momentan selektierte Kalender.
     * Dieser Wert ist immer gefüllt auch wenn keine KalenderGruppe ausgewählt ist. Wenn keine
     * Gruppe ausgewählt ist, beinhaltet dieses Eigenschaft den Kalender aus SelectedObject.
     */
    currentSelectedCalendar: modelsGet.ICalendar,

    /**
     * Alle benötigten Einstellungen.
     */
    settings: {
        eventDayViewTemplate: string
    },

    /**
     * Anzeige Text.
     */
    headerText: string,

    /**
     * Das zugeordnete Datum.
     */
    date: moment.Moment,

    /**
     * Alle Termine und sperren zu diesem Datum.
     */
    appointments: modelsGet.IAppointment[],

    /**
     * Angabe, ob das Item außerhalb des angezeigten Monats ist.
     */
    isOutsideCurrentMonth: boolean,

    /**
     * Angabe, ob das Item der heutige Tag ist.
     */
    isToday: boolean,

    /**
     * Angabe, ob der Tag selektiert ist.
     */
    isSelected: boolean,

    /**
     * Wird ausgeführt, sobald auf das Item geklickt wird.
     */
    onClick?: (date: moment.Moment) => void,

    /**
     * Wird ausgeführt sobald sich das Popup für die Termindetails öffnet.
     * @param e Das auslösende Ereignis.
     * @param appointment Der gebundene Termin oder die Sperre.
     */
    onAppointmentPopupOpening?: (e: MouseEvent, appointment: modelsGet.IAppointment) => boolean
}

interface IState
{
    windowHeight?: number,
    windowWidth?: number,
    eventsContainerHeight: number,
}

export class CalendarMonthDay extends React.Component<IProperties, IState>
{
    private _resizeTimeoutId: number;
    /**
     * Erstellt eine neue Instanz der Komponente @see CalendarMonthDay.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);
        this.state = {
            eventsContainerHeight: 0
        };
    }

    componentDidMount()
    {
        window.addEventListener("resize", this.updateDimensions);
    }

    componentWillUnmount()
    {
        window.removeEventListener("resize", this.updateDimensions);
    }

    private updateDimensions = () =>
    {
        clearTimeout(this._resizeTimeoutId);
        this._resizeTimeoutId = setTimeout(() =>
        {
            this.setState({
                eventsContainerHeight: $(".calendar_day_events").height()
            });
        }, 100);
    }

    private getEventsContainerHeight = (ref: HTMLElement) =>
    {
        if (ref != null && this.state.eventsContainerHeight <= 0)
        {
            this.setState({
                eventsContainerHeight: ref.offsetHeight
            });
        }
    }

    /**
     * Führt die "onClick" Eigenschaft aus wenn diese gesetzt wurde.
     * @param e Das auslösende Ereignis.
     */
    private onClick = (e: React.MouseEvent<HTMLElement>) =>
    {
        var { date, onClick } = this.props;
        onClick && onClick(date);
    }

    /**
     * Je nachdem was für eine Terminart übergeben wurde, wird entweder ein Termin oder
     * ein gesperrter Zeitraum zurückgegeben.
     * @param appointment Daten des Termins oder des gesperrten Zeitraums.
     */
    private renderAppointment(appointment: modelsGet.IAppointment): JSX.Element
    {
        const { currentUser, currentSelectedCalendar, settings } = this.props;

        if (appointment.appointment_type === "block")
        {
            return (
                <BlockedAppointment
                    currentSelectedCalendar={currentSelectedCalendar}
                    appointment={appointment}
                    isForMonthView={true}
                    isDisabled={!currentUser.claims.can_create_lock} />
            );
        }
        else
        {
            return (
                <Appointment
                    currentSelectedCalendar={currentSelectedCalendar}
                    eventDayViewTemplate={settings.eventDayViewTemplate}
                    appointment={appointment}
                    isForMonthView={true} />
            );
        }
    }

    /**
     * Render die Termine und/oder die gesperrten Zeiträume.
     */
    private renderAppointments(): JSX.Element[]
    {
        var comp: JSX.Element[] = [];

        if (this.state.eventsContainerHeight > 0)
        {
            var { date, appointments, onAppointmentPopupOpening, route } = this.props;

            // Die Anzahl ermitteln die im sichtbaren Bereich der Liste dargestellt werden kann.
            var screenItemsLen: number = Math.ceil(this.state.eventsContainerHeight / 16);
            screenItemsLen = screenItemsLen - 1;
            screenItemsLen = screenItemsLen < 0 ? 0 : screenItemsLen;

            for (var i = 0; i < appointments.length; i++)
            {
                //let isValid = isEventOrLockValid(date, eventsAndLocks[i], selectedObject);
                if (comp.length <= screenItemsLen)
                {
                    comp.push(
                        <PopupProvider
                            id="event-contextmenu"
                            trigger={PopupTrigger.RightClick}
                            triggerStopPropagation={true}
                            dataSource={{
                                calendar: 0,
                                appointment: appointments[i]
                            }}>
                            <PopupProvider
                                id="event-info"
                                trigger={PopupTrigger.Hover}
                                triggerDelay={1000}
                                triggerStopPropagation={true}
                                dataSource={appointments[i]}
                                onOpening={onAppointmentPopupOpening}>
                                {this.renderAppointment(appointments[i])}
                            </PopupProvider>
                        </PopupProvider>
                    );
                }
                else if (comp.length > screenItemsLen)
                {
                    var moreUri: string = route().setNewLocation({
                        date: date,
                        viewMode: CalendarViewMode.day
                    }).hrefRelative();

                    comp.push(
                        <OptionalLink
                            className="calendar_day_showmore mdi mdi-18px mdi-arrow-down-bold-circle"
                            title="mehr Termine anzeigen"
                            to={moreUri}>
                        </OptionalLink>
                    );
                    break;
                }
            }
        }

        return comp;
    }

    /**
     * Rendert die Komponente.
     */
    public render(): JSX.Element
    {
        var {
            style,
            route,
            currentUser,
            date,
            headerText,
            isOutsideCurrentMonth,
            isToday,
            isSelected
        } = this.props;

        var classNames: string[] = ["calendar_day"];

        if (isOutsideCurrentMonth)
            classNames.push("calendar_day--is-outside");

        if (isToday)
            classNames.push("calendar_day--is-today");

        if (isSelected)
            classNames.push("calendar_day--is-selected");

        var headerUri: string = route().setNewLocation({
            date: date,
            viewMode: CalendarViewMode.day
        }).hrefRelative();

        // Darf der Benutzer Termine in der Vergangenheit anlegen?.
        var contextMenuIsDisabled: boolean = false;
        if (!currentUser.claims.can_create_event_in_the_past)
        {
            if (date.isBefore(moment().startOf("day")))
                contextMenuIsDisabled = true;
        }

        if (!currentUser.claims.can_create_event && !currentUser.claims.can_create_lock)
            contextMenuIsDisabled = true;

        return (
            <div
                className={classNames.join(" ")}
                style={style}
                onClick={this.onClick}>
                <div className="calendar_day_header">
                    <OptionalLink
                        className="calendar_day_header_text"
                        to={headerUri}>
                        {headerText}
                    </OptionalLink>
                </div>
                <div
                    ref={this.getEventsContainerHeight}
                    className="calendar_day_events">
                    <PopupProvider
                        id="day-contextmenu"
                        className="calendar_day_events_popupcontainer"
                        trigger={PopupTrigger.RightClick}
                        dataSource={date}
                        isDisabled={contextMenuIsDisabled}>
                        {this.renderAppointments()}
                    </PopupProvider>
                </div>
            </div>
        );
    }
}